import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileQuestion, Plus, Download, CheckCircle2, XCircle, Upload } from "lucide-react";

const CATS = [
  { v: "documents_for_service", l: "Documents for Service" },
  { v: "supporting_evidence", l: "Supporting Evidence" },
  { v: "case_documents", l: "Case Documents" },
  { v: "completed_documents", l: "Completed Documents" },
];

const STATUS_TONE = {
  requested: "border-amber-200 bg-amber-50 text-amber-700",
  fulfilled: "border-emerald-200 bg-emerald-50 text-emerald-700",
  cancelled: "border-slate-200 bg-slate-50 text-slate-500",
};

export function DocumentRequests({ orderId, role = "client", orderStatus }) {
  const [reqs, setReqs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [desc, setDesc] = useState("");
  const [cat, setCat] = useState("documents_for_service");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/orders/${orderId}/document-requests`).then(({ data }) => setReqs(data)).catch(() => {});
  }, [orderId]);
  useEffect(load, [load]);

  const submit = async () => {
    if (!desc.trim()) { toast.error("Enter a document description"); return; }
    setBusy(true);
    try {
      await api.post(`/orders/${orderId}/document-requests`, { description: desc, category: cat, note });
      toast.success("Document requested");
      setDesc(""); setNote(""); setShowForm(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const cancel = async (id) => {
    try { await api.post(`/document-requests/${id}/cancel`); toast.success("Request cancelled"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const fulfill = async (req, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      // Server fulfills using an existing server-permitted vault category (never a restricted one).
      const catParam = "supporting_evidence";
      const { data: doc } = await api.post(`/orders/${orderId}/documents?category=${catParam}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/document-requests/${req.id}/fulfill`, { document_id: doc.id });
      toast.success("Request fulfilled");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="document-requests" className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="flex items-center gap-2 font-display text-base font-bold text-slate-900">
          <FileQuestion className="h-5 w-5 text-slate-400" /> Document Requests
        </h3>
        {role === "client" && orderStatus !== "completed" && (
          <Button data-testid="request-document-button" size="sm" onClick={() => setShowForm((s) => !s)} className="bg-slate-900 text-white hover:bg-slate-800">
            <Plus className="mr-1 h-3.5 w-3.5" /> Request Document
          </Button>
        )}
      </div>

      {showForm && role === "client" && (
        <div data-testid="request-form" className="mb-4 space-y-3 rounded-lg border border-slate-100 bg-slate-50 p-3">
          <Input data-testid="request-desc-input" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="What document do you need? (e.g. signed proof of service)" />
          <div className="flex flex-wrap gap-2">
            <Select value={cat} onValueChange={setCat}>
              <SelectTrigger data-testid="request-category-select" className="w-56 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>{CATS.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Textarea data-testid="request-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Optional instructions/note" />
          <Button data-testid="submit-request-button" size="sm" onClick={submit} disabled={busy} className="bg-emerald-600 text-white hover:bg-emerald-700">{busy ? "Sending…" : "Send request"}</Button>
        </div>
      )}

      {reqs.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-400">No document requests yet.</p>
      ) : (
        <div className="space-y-2">
          {reqs.map((r) => (
            <div key={r.id} data-testid={`request-row-${r.id}`} className="rounded-lg border border-slate-100 px-3 py-2.5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-800">{r.description}</p>
                  <p className="text-xs text-slate-400">
                    {new Date(r.created_at).toLocaleDateString()} · {r.created_by}
                    {r.fulfilled_at ? ` · fulfilled ${new Date(r.fulfilled_at).toLocaleDateString()}` : ""}
                  </p>
                  {r.note && <p className="mt-1 text-xs italic text-slate-400">“{r.note}”</p>}
                </div>
                <span data-testid={`request-status-${r.id}`} className={`shrink-0 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATUS_TONE[r.status]}`}>
                  {r.status}
                </span>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {r.status === "fulfilled" && r.fulfilled_document_id && (
                  <a data-testid={`request-doc-${r.id}`} href={`${API}/files/${r.fulfilled_document_id}`} target="_blank" rel="noreferrer">
                    <Button size="sm" variant="outline"><Download className="mr-1 h-3.5 w-3.5" /> Fulfilled document</Button>
                  </a>
                )}
                {role === "server" && r.status === "requested" && (
                  <label data-testid={`fulfill-request-${r.id}`} className="cursor-pointer">
                    <input type="file" className="hidden" onChange={(e) => fulfill(r, e)} />
                    <span className="inline-flex h-8 items-center rounded-md bg-slate-900 px-3 text-xs font-semibold text-white hover:bg-slate-800"><Upload className="mr-1 h-3.5 w-3.5" /> Upload & fulfill</span>
                  </label>
                )}
                {(role === "client" || role === "admin") && r.status === "requested" && (
                  <Button data-testid={`cancel-request-${r.id}`} size="sm" variant="outline" onClick={() => cancel(r.id)}>
                    <XCircle className="mr-1 h-3.5 w-3.5" /> Cancel
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
